import { NavLink, useNavigate } from 'react-router-dom'
import { Home, FileText, BarChart3, Settings, ChevronLeft, ChevronRight, Zap, Users, Layers, Sparkles } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import PlanBadge from './ui/PlanBadge'

const STUDENT_NAV = [
  { name: 'Dashboard',     href: '/',          icon: Home,      end: true  },
  { name: 'Materiales',    href: '/materials', icon: FileText,  end: false },
  { name: 'Cursos',        href: '/courses',   icon: Layers,    end: false },
  { name: 'Repaso',        href: '/review',    icon: Sparkles,  end: false },
  { name: 'Resultados',    href: '/results',   icon: BarChart3, end: false },
  { name: 'Configuración', href: '/settings',  icon: Settings,  end: false },
]

const PARENT_NAV = [
  { name: 'Dashboard',     href: '/',       icon: Home,     end: true  },
  { name: 'Mis hijos',     href: '/parent', icon: Users,    end: false },
  { name: 'Configuración', href: '/settings', icon: Settings, end: false },
]

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const isFree = !user?.plan || user.plan === 'free'
  const navigation = user?.role === 'parent' ? PARENT_NAV : STUDENT_NAV

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?'

  return (
    <aside
      className={`fixed left-0 top-0 h-screen flex flex-col z-20 transition-all duration-300 ease-smooth ${
        collapsed ? 'w-[72px]' : 'w-[240px]'
      }`}
      style={{
        background: 'var(--color-sidebar)',
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center space-x-3 overflow-hidden">
          {/* K mark */}
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-white text-sm"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}
          >
            K
          </div>
          {!collapsed && (
            <span className="font-bold text-white text-lg tracking-tight whitespace-nowrap animate-fade-in">
              kapri
            </span>
          )}
        </div>

        {/* Toggle button */}
        <button
          onClick={onToggle}
          className="ml-auto p-1.5 rounded-lg transition-colors flex-shrink-0"
          style={{ color: 'var(--color-text-muted)' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.end}
            title={collapsed ? item.name : undefined}
            className={({ isActive }) =>
              `flex items-center rounded-xl transition-all duration-150 group ${
                collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5 space-x-3'
              } ${
                isActive
                  ? 'bg-gradient-to-r from-[rgba(79,70,229,0.2)] to-[rgba(79,70,229,0.05)] text-indigo-300'
                  : 'text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-white/5'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon
                  className={`w-5 h-5 flex-shrink-0 transition-colors ${
                    isActive ? 'text-indigo-400' : 'text-[#64748B] group-hover:text-[#94A3B8]'
                  }`}
                />
                {!collapsed && (
                  <span className="text-sm font-medium whitespace-nowrap">{item.name}</span>
                )}
                {!collapsed && isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Upgrade CTA for free users */}
      {isFree && !collapsed && (
        <div className="px-3 pb-2">
          <button
            onClick={() => navigate('/pricing')}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: 'linear-gradient(135deg, rgba(79,70,229,0.15), rgba(139,92,246,0.15))',
              border: '1px solid rgba(99,102,241,0.3)',
              color: '#A5B4FC',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.6)' }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)' }}
          >
            <Zap className="w-3.5 h-3.5" />
            Actualizar a Pro
          </button>
        </div>
      )}

      {/* User footer */}
      <div
        className={`flex-shrink-0 p-3 ${collapsed ? 'flex justify-center' : ''}`}
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        {collapsed ? (
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}
            title={user?.name}
          >
            {initials}
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#E2E8F0] truncate">{user?.name}</p>
              <PlanBadge plan={user?.plan ?? 'free'} size="sm" />
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}
